<!-- eslint-disable vue/max-len max-len -->
<template>
  <v-dialog v-model="uiModalsStore.displayNewVersionsWindow" :width="1000" height="100%" persistent @keydown.esc="close">
    <v-card class="pa-6 px-sm-12 pr-md-0 light-scrollbar h-100">
      <div class="d-flex flex-wrap flex-md-nowrap gap-4 h-100">
        <div>
          <template v-if="$i18n.locale == 'fr'">
            <div style="font-size: 28px">La fiche projet fait peau neuve !</div>
            <div class="py-4 d-flex flex-column gap-4">
              <div>Suivez vos projets plus facilement, retrouvez l'information plus vite et préparez vos points de pilotage sans effort.</div>
              <v-expansion-panels v-model="openedPanel" flat static class="ml-n4">
                <v-expansion-panel>
                  <v-expansion-panel-title>
                    <div>
                      <b>📋 Une fiche projet plus claire et paramétrable</b>
                      <div class="mt-2"><i>Concentrez les informations essentielles de vos projets</i></div>
                    </div>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    La fiche projet a été refondue : objectifs, budget, points de suivi, documents, activité et intervenants sont organisés en blocs dédiés. Vous gagnez en lisibilité et pouvez adapter l'affichage à vos priorités en masquant ou en réorganisant les blocs (BUSINESS).
                  </v-expansion-panel-text>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-title>
                    <div>
                      <b>📊 Des objectifs plus faciles à suivre</b>
                      <div class="mt-2"><i>Identifiez plus rapidement les écarts et l'évolution du projet</i></div>
                    </div>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    Les objectifs de date et de budget sont plus visibles, avec une comparaison immédiate entre le prévu et le réalisé. Le budget bénéficie d'une synthèse graphique, et les points de suivi peuvent être consultés sous forme visuelle pour mieux tracer l'évolution du projet.
                  </v-expansion-panel-text>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-title>
                    <div>
                      <b>🔎 Une synthèse budgétaire plus efficace</b>
                      <div class="mt-2"><i>Filtrez, analysez et décidez</i></div>
                    </div>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    Le rapport budgétaire de la fiche projet vous permet d'aller plus vite à l'essentiel : filtres sur les classes budgétaires, synthèse visuelle, et comparaison au budget cible. Vous retrouvez plus rapidement les informations utiles pour analyser et décider.
                  </v-expansion-panel-text>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-title>
                    <div>
                      <b>⚠️ Un module de suivi des risques (ENTERPRISE)</b>
                      <div class="mt-2"><i>Structurez les points de vigilance avant vos revues projet</i></div>
                    </div>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    La nouvelle matrice des risques de la fiche projet vous aide à structurer les forces, faiblesses, opportunités et menaces du projet. Très flexible, vous pouvez l'adapter à vos usages. Pratique pour préparer une revue, partager les points de vigilance et garder une vision claire des sujets sensibles.
                  </v-expansion-panel-text>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-title>
                    <div>
                      <b>🧩 Une gestion plus rapide des sous-projets (ENTERPRISE)</b>
                      <div class="mt-2"><i>Mettez à jour vos vues consolidées en 1 clic</i></div>
                    </div>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    La nouvelle interface de gestion des sous-projets facilite les mises à jour en masse : recherchez, filtrez, ajoutez ou retirez plusieurs projets en quelques actions. Idéal pour maintenir des vues consolidées propres et à jour.
                  </v-expansion-panel-text>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-title>
                    <div>
                      <b>💡 De nouveaux supports d'aide pour vous accompagner</b>
                      <div class="mt-2"><i>De nombreuses nouvelles vidéos et fiches astuces sont disponibles</i></div>
                    </div>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    De nouvelles vidéos et fiches astuces sont disponibles dans l'aide, avec des repères <b>NEW</b> pour identifier rapidement les contenus récents et découvrir les bonnes pratiques au bon moment.
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </template>
          <template v-else>
            <div style="font-size: 28px">The project sheet has a new look!</div>
            <div class="py-4 d-flex flex-column gap-4">
              <div>Follow your projects more easily, find information faster and prepare your steering points effortlessly.</div>
              <v-expansion-panels v-model="openedPanel" flat static class="ml-n4">
                <v-expansion-panel>
                  <v-expansion-panel-title>
                    <div>
                      <b>📋 A clearer and configurable project sheet</b>
                      <div class="mt-2"><i>Focus on the essential information of your projects</i></div>
                    </div>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    The project sheet has been redesigned: objectives, budget, follow-up points, documents, activity and stakeholders are organized into dedicated blocks. You gain in readability and can adapt the display to your priorities by hiding or reorganizing the blocks (BUSINESS).
                  </v-expansion-panel-text>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-title>
                    <div>
                      <b>📊 Easier to track objectives</b>
                      <div class="mt-2"><i>Identify gaps and project evolution more quickly</i></div>
                    </div>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    The date and budget objectives are more visible, with an immediate comparison between planned and actual. The budget benefits from a graphical summary, and the follow-up points can be viewed visually to better track the project's evolution.
                  </v-expansion-panel-text>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-title>
                    <div>
                      <b>🔎 More efficient budget summary</b>
                      <div class="mt-2"><i>Filter, analyze and decide</i></div>
                    </div>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    The budget report of the project sheet allows you to get to the essentials faster: filters on budget classes, visual summary, and comparison to target budget. You can find useful information more quickly to analyze and make decisions.
                  </v-expansion-panel-text>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-title>
                    <div>
                      <b>⚠️ A risk tracking module (ENTERPRISE)</b>
                      <div class="mt-2"><i>Structure the points of vigilance before your project reviews</i></div>
                    </div>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    The new risk matrix of the project sheet helps you structure the strengths, weaknesses, opportunities and threats of the project. Very flexible, you can adapt it to your uses. Useful for preparing a review, sharing points of vigilance and keeping a clear view of sensitive issues.
                  </v-expansion-panel-text>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-title>
                    <div>
                      <b>🧩 Faster management of sub-projects (ENTERPRISE)</b>
                      <div class="mt-2"><i>Update your consolidated views in 1 click</i></div>
                    </div>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    The new sub-project management interface makes it easier to update in bulk: search, filter, add or remove multiple projects in a few actions. Ideal for keeping consolidated views clean and up to date.
                  </v-expansion-panel-text>
                </v-expansion-panel>

                <v-expansion-panel>
                  <v-expansion-panel-title>
                    <div>
                      <b>💡 New help resources to support you</b>
                      <div class="mt-2"><i>Access guides, tutorials, and tips</i></div>
                    </div>
                  </v-expansion-panel-title>
                  <v-expansion-panel-text>
                    We have added new help resources to assist you in using the platform more effectively. Explore guides, tutorials, and tips to get the most out of your experience.
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </template>
        </div>
        <div class="w-100 text-center px-6 pt-2">
          <img src="./img/newversion.svg" class="hidden-sm-and-down mb-12" style="max-width: 260px">
          <v-btn color="primary" size="large" rounded @click="close">
            {{ $t('HELP.NEW_VERSION_BUTTON') }}
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
  import useUiModalsStore from '@/stores/ui/modals';
  import darkModeMixin from '@/components/Manage/ManageProfile/darkModeMixin';

  export default {
    mixins: [darkModeMixin],
    data() {
      return {
        openedPanel: null,
        uiModalsStore: useUiModalsStore(),
      };
    },
    mounted() {
      setTimeout(() => {
        if (! this.openedPanel) this.openedPanel = 0;
      }, 600);
    },
    methods: {
      close() {
        this.uiModalsStore.setDisplayNewVersionsWindow(false);
      },
    },
  };
</script>
